<template>
  <b-layout :colors="$site.colors" :header-title="$t('pages.book.availability.title')" :header-back-route="backRoute" :breadcrumbs="breadcrumbs">
    <b-container class="mt-12">
      <b-summary>
        <template #summary>
          <booking-detail :stage="stage" />
        </template>

        <div>
          <b-errors :errors="errors" class="mb-3" />

          <voucher-alert />
          <pass-alert />

          <b-card
            :title="$t('pages.book.availability.selectDate')"
            class="mb-12"
            content-class="w-full flex flex-col justify-between xs:p-4 rounded border bg-primary relative"
          >
            <template #control>
              <b-button :disabled="checking" variant="action" size="sm" @click="nextAvailable">
                <div class="flex items-center space-x-2  rtl:space-x-reverse">
                  <template v-if="checking">
                    <b-spinner />
                    {{ checking.month }}, {{ checking.year }}
                  </template>
                  <template v-else>
                    <b-icon icon="search" base-class="h-4 w-4 mr-2" />
                    {{ $t('pages.book.availability.nextAvailable') }} »
                  </template>
                </div>
              </b-button>
            </template>

            <div class="w-100 overflow-x-auto">
              <div v-if="datesLoading > 0" class="absolute inset-0 flex items-center justify-center">
                <b-spinner />
              </div>

              <VCalendar
                ref="calendar"
                v-model="selectedDate"
                expanded
                :attributes="attributes"
                :style="{ opacity: datesLoading > 0 ? '0.5' : '1' }"
                :locale="$i18n.locale"
                :min-date="now"
                :columns="cols"
                :select-attribute="{
                  highlight: {
                    style: {
                      backgroundColor: $site.colors.action.background,
                      borderColor: $site.colors.action.background,
                      borderRadius: '5px',
                      width: '100%',
                      height: '100%'
                    },
                    contentStyle: {
                      color: $site.colors.action.text,
                    }
                  }
                }"
                class="max-[280px]:scale-[0.85] origin-top-left"
                @dayclick="dayClicked"
                @did-move="updatePage"
              />
            </div>
          </b-card>
          <b-card
            :title="(selectedDate) ? $format.date(selectedDate.getTime() / 1000, 'local') : ''"
            content-class="w-full"
            class="mb-8"
          >
            <div v-if="error" class="error">
              {{ error }}
            </div>

            <div v-if="timesLoading" class="loading">
              <div class="text-center">
                <b-spinner label="Loading" class="m-3 bookapp-action-tint-color bookapp-spinner-large" type="grow" />
              </div>
            </div>

            <div v-if="!timesLoading && availableTimes" class="w-full">
              <div v-if="availableTimes.length == 0">
                <div v-if="selectedDateWaitlistable">
                  <p class="text-secondary mb-2 text-sm">
                    {{ $t('pages.book.availability.noAvailabilityWithWaitlist') }}
                  </p>
                  <br>
                  <b-button type="button" class="w-full" variant="action" @click="addToWaitlist">
                    {{ $t('pages.book.availability.addToWaitlist') }} »
                  </b-button>
                </div>
                <p v-else class="text-secondary mb-2 text-sm">
                  {{ $t('pages.book.availability.noAvailability') }}
                </p>

                <b-button :disabled="checking" variant="action" class="w-full mt-2" @click="nextAvailable">
                  <div class="flex items-center space-x-2  rtl:space-x-reverse">
                    <template v-if="checking">
                      <b-spinner />
                      {{ checking.month }}, {{ checking.year }}
                    </template>
                    <template v-else>
                      <b-icon icon="search" base-class="h-4 w-4 mr-2" />
                      {{ $t('pages.book.availability.nextAvailable') }} »
                    </template>
                  </div>
                </b-button>
              </div>
              <div v-else>
                <div class="sm:flex items-start sm:space-x-4  rtl:space-x-reverse">
                  <div v-if="availableMorningTimes && availableMorningTimes.length" class="w-full sm:w-1/2">
                    <h5 class="text-xs uppercase text-secondary/70 tracking-wider mb-3">
                      {{ $t('datetime.timeOfDay.morning') }}
                    </h5>
                    <ul class="times-list mt-2">
                      <template v-for="slot in availableMorningTimes" :key="slot.data">
                        <li>
                          <UTooltip
                            :text="slotTooltip(slot)"
                            :popper="{ placement: 'top' }"
                            class="w-full"
                          >
                            <button
                              :class="[
                                'w-full rounded font-semibold hover:bg-action hover:text-action hover:shadow transition-all p-2',
                                isActionLight ? 'bg-action/80 text-action' : 'bg-action/10 text-primary'
                              ]"
                              @click="slotClicked(slot)"
                            >
                              {{ $format.minuteTime(slot.startTime) }}

                              <b-chip v-if="slot.classStatus === 'almost-full'" class="ml-3">
                                {{ $t('general.almostFull') }}
                              </b-chip>
                              <b-chip v-if="slot.classStatus === 'full'" class="ml-3">
                                {{ $t('general.full') }} <span v-if="selectedDateWaitlistable">- {{ $t('general.waitlist') }}</span>
                              </b-chip>
                              <b-chip v-if="slot.totalDelay != 0" class="ml-3">
                                {{ $t('general.waitTime', {time: slot.totalDelay}) }}
                              </b-chip>
                            </button>
                          </UTooltip>
                        </li>
                      </template>
                    </ul>
                  </div>
                  <div v-if="availableAfternoonTimes && availableAfternoonTimes.length" class="w-full sm:w-1/2">
                    <h5 class="text-xs uppercase text-secondary/70 tracking-wider mb-3">
                      {{ $t('datetime.timeOfDay.afternoon') }}
                    </h5>
                    <ul class="times-list text-center mt-2">
                      <li v-for="slot in availableAfternoonTimes" :key="slot.data">
                        <UTooltip
                          :text="slotTooltip(slot)"
                          :popper="{ placement: 'top' }"
                          class="w-full"
                        >
                          <button
                            :class="[
                              'w-full rounded font-semibold hover:bg-action hover:text-action hover:shadow transition-all p-2',
                              isActionLight ? 'bg-action/80 text-action' : 'bg-action/10 text-primary'
                            ]"
                            @click="slotClicked(slot)"
                          >
                            {{ $format.minuteTime(slot.startTime) }}

                            <b-chip v-if="slot.classStatus === 'almost-full'" class="ml-3">
                              {{ $t('general.almostFull') }}
                            </b-chip>
                            <b-chip v-if="slot.classStatus === 'full'" class="ml-3">
                              {{ $t('general.full') }} <span v-if="selectedDateWaitlistable">- {{ $t('general.waitlist') }}</span>
                            </b-chip>
                            <b-chip v-if="slot.totalDelay != 0" class="ml-3">
                              {{ $t('general.waitTime', {time: slot.totalDelay}) }}
                            </b-chip>
                          </button>
                        </UTooltip>
                      </li>
                    </ul>
                  </div>
                </div>
                <div v-if="!isGroupService && selectedDateWaitlistable" class="mt-6">
                  <p class="text-secondary mb-2 text-sm">
                    {{ $t('pages.book.availability.noSuitableTimes') }}
                  </p>
                  <br>
                  <b-button type="button" class="w-full" variant="action" @click="addToWaitlist">
                    {{ $t('pages.book.availability.addToWaitlist') }} »
                  </b-button>
                </div>
              </div>
            </div>
          </b-card>
        </div>
      </b-summary>
    </b-container>
  </b-layout>
</template>

<script>
import { addMonths, addDays, addMinutes, startOfDay } from "date-fns";
import { DatePicker as VCalendar } from '@angelblanco/v-calendar';
import '@angelblanco/v-calendar/dist/style.css'
import Color from 'color'

import { useScreens } from "vue-screen-utils";


import BookingDetail, { BookingStage, createBookingRoute } from '@/components/booking/elements/Detail.vue'
import VoucherAlert from '@/components/voucher/elements/Alert.vue'
import PassAlert from '@/components/pass/elements/Alert.vue'

definePageMeta({
  tabs: ['online'],
  middleware: ['redirects'],
  layout: 'default'
});

export default defineNuxtComponent({
  components: {
    VCalendar,
    BookingDetail,
    VoucherAlert,
    PassAlert
  },
  async asyncData () {
    const { $api } = useNuxtApp()
    const route = useRoute()
    const locationStore = useLocationStore()
    const bookingStore = useBookingStore()

    await locationStore.getServices()
    await bookingStore.setFromUrlQuery(route.query)

    if (bookingStore.selectedServices.length === 0) {
      navigateTo({ path: '/book-now' })
    }

    return {}
  },
  data () {
    return {
      stage: BookingStage.AVAILABILITY,
      datesLoading: 0,
      timesLoading: false,
      error: null,
      errors: [],
      times: null,
      selectedSlot: null,
      dateData: [],
      availableTimes: null,
      selectedDateLabel: null,
      selectedDateWaitlistable: false,
      checking: null
    }
  },
  computed: {
    selectedDate() {
      const bookingStore = useBookingStore()
      return bookingStore.selectedDate || new Date()
    },
    selectedServices() {
      const bookingStore = useBookingStore()
      return bookingStore.selectedServices
    },
    moveReservation() {
      const bookingStore = useBookingStore()
      return bookingStore.moveReservation
    },
    isActionLight () {
      const action = Color(this.$site.colors.action?.background.trim())
      return action.isLight()
    },
    cols () {
      const { mapCurrent } = useScreens({ xs: '0px', sm: '640px', md: '768px', lg: '1024px' })
      return mapCurrent({ lg: 2 }, 1).value
      // return 2
    },
    backRoute () {
      const bookingUrlQuery = this.$store.getters['booking/urlQuery']
      return createBookingRoute(bookingUrlQuery, BookingStage.SERVICES)
    },
    breadcrumbs () {
      return [
        { text: this.$site.tabs.getName('book', 'Book'), to: this.backRoute },
        { text: this.$t('pages.book.availability.title'), active: true },
        { text: this.$t('pages.book.customer.title'), disabled: true },
        { text: this.$t('pages.book.confirm.title'), disabled: true }
      ]
    },
    now () {
      const now = new Date()
      now.setHours(0, 0, 0, 0)
      return now
    },
    availableDates () {
      return this.dateData.filter((dateInfo) => {
        return (dateInfo.available && dateInfo.date >= this.now)
      }).map((dateInfo) => {
        return new Date(dateInfo.date.getUTCFullYear(), dateInfo.date.getUTCMonth(), dateInfo.date.getUTCDate(), 0, 0, 0, 0)
      })
    },
    availableMorningTimes () {
      const offeredSlot = []

      const availableTimes = [...this.availableTimes]

      return availableTimes
        .sort((aSlot, bSlot) => {
          return bSlot.available - aSlot.available
        })
        .filter((slot) => {
          if (!(slot.startTime < 720) || offeredSlot.includes(slot.slot)) { return false }

          offeredSlot.push(slot.slot)
          return true
        })
    },
    availableAfternoonTimes () {
      const offeredSlot = []

      const availableTimes = [...this.availableTimes]

      return availableTimes
        .sort((aSlot, bSlot) => {
          return bSlot.available - aSlot.available
        })
        .filter((slot) => {
          if (!(slot.startTime >= 720) || offeredSlot.includes(slot.slot)) { return false }

          offeredSlot.push(slot.slot)
          return true
        })
    },
    isGroupService () {
      return this.selectedServices.filter(s => s.service.customerType === 1).length > 0
    },
    attributes () {
      return [
        {
          dot: {
            class: 'available',
            style: {
              backgroundColor: this.$site.colors.action.background,
              marginBottom: '2px'
            }
          },
          dates: this.availableDates
        },
        {
          highlight: {
            style: {
              backgroundColor: this.$site.colors.action.background + '99',
              borderRadius: '5px',
              width: '100%',
              height: '100%'
            },
            contentStyle: {
              color: this.$site.colors.action.text
            }
          },
          dates: [new Date()]
        }
      ]
    },
    voucher () {
      return this.$store.getters['booking/voucher']
    },
    pass () {
      return this.$store.getters['booking/pass']
    }
  },
  watch: {
    voucher () {
      this.fetchAvailableDates(this.selectedDate)
      this.fetchAvailableTimes(this.selectedDate)
    },
    pass () {
      this.fetchAvailableDates(this.selectedDate)
      this.fetchAvailableTimes(this.selectedDate)
    }
  },
  methods: {
    slotTooltip (slot) {
      let tooltip = ''
      let priceFrom = false
      for (let i = 0; i < slot.services.length; i++) {
        const slotService = slot.services[i]

        //  tooltip += // slotService.service.title + ' with ' + slotService.employee.firstName
        tooltip += this.$t('general.serviceWithEmployee', {
          service: slotService.service.title,
          employee: slotService.employee.displayName
        })

        priceFrom = priceFrom || slotService.service.priceFrom
      }

      if (this.$site.settings.bookShowServicePrices && slot.totalPrice > 0) {
        tooltip += ': ' + (priceFrom ? (this.$t('general.from') + ' ') : '') + slot.totalPriceLabel
      }

      return tooltip
    },
    async fetchAvailableDates (date) {
      this.datesLoading = this.datesLoading + 1
      const response = await this.$api.booking.monthAvailability(
        date,
        this.selectedServices,
        this.moveReservation,
        (this.voucher && this.voucher.isValid) ? this.voucher : null,
        (this.pass && this.pass.isValid) ? this.pass : null
      )
      this.datesLoading = this.datesLoading - 1

      if (response.dates) {
        for (let i = 0; i < response.dates.length; i++) {
          const date = response.dates[i]

          const contains = this.dateData.some((dateInfo) => {
            return (dateInfo.date.getTime() === date.date.getTime())
          })

          if (!contains) {
            this.dateData.push(date)
          }
        }
      }
    },
    async fetchAvailableTimes (date) {
      this.timesLoading = true
      this.availableTimes = null
      this.selectedDateLabel = null
      this.selectedDateWaitlistable = false
      this.error = null

      try {
        const response = await this.$api.booking.dayAvailability(
          date,
          this.selectedServices,
          this.moveReservation,
          (this.voucher && this.voucher.isValid) ? this.voucher : null,
          (this.pass && this.pass.isValid) ? this.pass : null
        )

        this.selectedDateLabel = response.dateLabel
        this.selectedDateWaitlistable = response.waitlistable
        this.availableTimes = (response.slots) ? response.slots : []
      } catch (error) {
        this.error = this.$t('general.errorRequest')
      }

      this.timesLoading = false
    },
    async nextAvailable () {
      this.errors = []
      // const selectedDatePlusOne = this.$moment(this.selectedDate).add(1, 'days').startOf('date').toDate()
      const selectedDatePlusOne = startOfDay(addDays(this.selectedDate, 1))

      // let date = this.$moment(this.selectedDate).startOf('day')
      let date = startOfDay(this.selectedDate)
      let found = false
      let i = 0

      while (!found && i < 3) {
        this.checking = {
          month: date.toLocaleString(this.$i18n.locale, { month: 'long' }),
          year: date.toLocaleString(this.$i18n.locale, { year: 'numeric' })
        }
        this.datesLoading = this.datesLoading + 1
        const response = await this.$api.booking.monthAvailability(
          // date.toDate(),
          date,
          this.selectedServices,
          this.moveReservation,
          (this.voucher && this.voucher.isValid) ? this.voucher : null,
          (this.pass && this.pass.isValid) ? this.pass : null
        )
        this.datesLoading = this.datesLoading - 1

        if (response.dates) {
          for (let i = 0; i < response.dates.length; i++) {
            const date = response.dates[i]
            const _date = new Date(date.date)
            const utcDate = addMinutes(_date, _date.getTimezoneOffset())

            const match = date.available && selectedDatePlusOne <= utcDate

            if (match) {
              found = true

              if (this.$refs.calendar) {
                this.$refs.calendar.move(utcDate)
              }

              const bookingStore = useBookingStore()
              bookingStore.selectedDate = utcDate
              this.fetchAvailableTimes(this.selectedDate)
              break
            }
          }
        }

        i++
        // date = date.add(1, 'months')
        date = addMonths(date, 1)
      }

      this.checking = null

      if (!found) {
        this.errors.push(this.$t('pages.book.availability.nothingFound'))
      }
    },
    dayClicked (day, event) {
      event.target.blur()
      
      this.errors = []

      if (day.date >= this.now) {
        this.$store.dispatch('booking/setSelectedDate', day.date)

        this.selectedDate = day.date
        this.fetchAvailableTimes(this.selectedDate)
      }
    },
    updatePage ([page]) {
      const d = new Date(page.year, page.month - 1, 1, 0, 0, 0, 0)
      this.fetchAvailableDates(d)
    },
    slotClicked (slot) {
      this.errors = []
      if (slot.available) {
        this.$store.dispatch('booking/setSelectedSlot', { slot })
        const bookingUrlQuery = this.$store.getters['booking/urlQuery']

        const isGroup = (slot.services.length === 1 && slot.services[0].service.customerType === 1)
        if (isGroup && this.$site.settings.showGroupDetail) {
          navigateTo(createBookingRoute(bookingUrlQuery, BookingStage.DETAIL))
        } else {
          navigateTo(createBookingRoute(bookingUrlQuery, BookingStage.CUSTOMER))
        }
      } else if (this.selectedDateWaitlistable) {
        this.addToWaitlist()
      }
    },
    addToWaitlist () {
      if (this.selectedDate) {
        const selectedDate = new Date(this.selectedDate.getTime() - (this.selectedDate.getTimezoneOffset() * 60000))

        this.$store.dispatch('booking/setSelectedDate', selectedDate)
        const bookingUrlQuery = this.$store.getters['booking/urlQuery']

        navigateTo(createBookingRoute(bookingUrlQuery, BookingStage.WAITLIST_CUSTOMER))
      }
    }
  },
  mounted () {
    this.fetchAvailableDates(this.selectedDate)
    this.fetchAvailableTimes(this.selectedDate)
  }
})
</script>

<style>
.times-list {
  list-style: none; padding: 0; margin: 0;
}

.times-list li {
  margin-bottom:10px;
}
</style>